import { useEffect, useMemo, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import DashboardLayout from "../../layouts/DashboardLayout";
import localeText from "../../../../locale";
import GetInfoService from "../../../../service/GetInfoService";
import GetUserInfoService from "../../../../service/GetUserInfoService";
import AreaManagerService from "../../../../service/Cadastros/AreaManagerService";
import { formatPrice } from "../../../../utilities/formatPrice";
import UpgradePlanCard from "./components/UpgradePlanCard";
import UpgradePaymentCheckoutStep from "./components/UpgradePaymentCheckoutStep";
import UpgradePaymentRedirectStep from "./components/UpgradePaymentRedirectStep";
import "./UpgradePage.css";

const UPGRADE_FLOW_ROLES = ["Customer", "Partner", "Seller", "Store"];
const UPGRADE_MENU_ROLES = ["Customer", "Partner", "Seller", "Store"];
const STORE_START_SELLER_PRICE = 350;
const normalizeCurrencyCode = (currency) => {
  const rawCurrency = (currency || "").toUpperCase();
  if (rawCurrency.length === 3) return rawCurrency;
  if (rawCurrency === "$") return "USD";
  if (rawCurrency === "€") return "EUR";
  if (rawCurrency === "£") return "GBP";
  if (rawCurrency === "R$") return "BRL";
  return "EUR";
};

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const isPlanAllowedByRules = (rulesResult, planKey, userPlan) => {
  const normalizedRulesPlan = normalizeText(rulesResult?.plan);
  const normalizedUserPlan = normalizeText(userPlan);
  const normalizedPlanKey = normalizeText(planKey);

  if (normalizedUserPlan === "store" && normalizedPlanKey === "startseller") {
    return normalizedRulesPlan !== "startseller";
  }

  const upgrades = Array.isArray(rulesResult?.upgrades) ? rulesResult.upgrades : [];
  if (!upgrades.length) return false;

  const normalizedUpgrades = upgrades.map(normalizeText);

  if (normalizedPlanKey === "partner") {
    return normalizedUpgrades.some(
      (name) => name.includes("partner") || name.includes("silverpro"),
    );
  }

  if (normalizedPlanKey === "manager") {
    return normalizedUpgrades.some((name) => name.includes("manager") || name.includes("geogold"));
  }

  if (normalizedPlanKey === "startseller") {
    return normalizedUpgrades.some((name) => name.includes("startseller"));
  }

  if (normalizedPlanKey === "seller") {
    if (normalizedUserPlan === "store") {
      return normalizedUpgrades.some((name) => name.includes("startseller"));
    }
    return normalizedUpgrades.some((name) => name.includes("seller"));
  }

  return true;
};

const PLAN_CONFIG = {
  Partner: {
    id: 1,
    code: "partner",
    learnMorePath: "/businesspartner",
    tierLabel: "PRO SILVER",
    featureKeys: [
      "upgradeFeatureStorefront",
      "upgradeFeatureEconomicCircle",
      "upgradeFeatureNoMonthlyFee",
    ],
  },
  Seller: {
    id: 4,
    code: "seller",
    learnMorePath: "/seller",
    tierLabel: "START SELLER",
    featureKeys: [
      "upgradeFeaturePersonalEcommerce",
      "upgradeFeatureDropshipping",
      "upgradeFeatureEconomicCircle",
      "upgradeFeatureNoMonthlyFee",
    ],
  },
  Manager: {
    id: 2,
    code: "manager",
    learnMorePath: "/areamanager",
    tierLabel: "GEO GOLD",
    featureKeys: [
      "upgradeFeatureOnlineStore",
      "upgradeFeatureGeolocationActive",
      "upgradeFeatureAllIncluded",
      "upgradeFeatureNoMonthlyFee",
    ],
  },
  StartSeller: {
    id: 4,
    code: "startseller",
    learnMorePath: "/seller",
    tierLabel: "START SELLER",
    featureKeys: [
      "upgradeFeaturePersonalEcommerce",
      "upgradeFeatureDropshipping",
      "upgradeFeatureEconomicCircle",
      "upgradeFeatureNoMonthlyFee",
    ],
  },
};

const UPGRADE_OPTIONS_BY_ROLE = {
  Customer: ["Partner", "Seller", "Manager"],
  Partner: ["Seller", "Manager"],
  Seller: ["Manager"],
  Store: ["StartSeller"],
};

const HIGHLIGHT_BY_ROLE = {
  Customer: "Partner",
  Partner: "Seller",
  Seller: "Manager",
  Store: "StartSeller",
};

const UpgradePage = ({ userTypeInfo, currency, canAccessUpgradePage = true }) => {
  const getInfoService = useMemo(() => new GetInfoService(), []);
  const getUserInfoService = useMemo(() => new GetUserInfoService(), []);
  const areaManagerService = useMemo(() => new AreaManagerService(), []);
  const toastUpgrade = useRef(null);

  const [plan, setPlan] = useState(0);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [loadingBusiness, setLoadingBusiness] = useState(false);
  const [loadingArea, setLoadingArea] = useState(false);
  const [loadingSeller, setLoadingSeller] = useState(false);
  const [priceForex, setPriceForex] = useState(0);
  const [priceBusiness, setPriceBusiness] = useState(0);
  const [priceArea, setPriceArea] = useState(0);
  const [priceSeller, setPriceSeller] = useState(0);
  const [voucher, setVoucher] = useState("");
  const [redirectCounting, setRedirectCounting] = useState(false);
  const [redirectCounter, setRedirectCounter] = useState(20);
  const [useVoucherCheck, setUseVoucherCheck] = useState(false);
  const [allowedUpgradeKeys, setAllowedUpgradeKeys] = useState(null);

  const userPlan = userTypeInfo;
  const effectiveCurrency = normalizeCurrencyCode(currency);
  const hasUpgradeAccess = UPGRADE_MENU_ROLES.includes(userPlan);
  const hasPaymentFlow = UPGRADE_FLOW_ROLES.includes(userPlan);
  const hasRouteAccess = canAccessUpgradePage;

  const upgradeOptions = useMemo(() => UPGRADE_OPTIONS_BY_ROLE[userPlan] || [], [userPlan]);
  const highlightedPlan = HIGHLIGHT_BY_ROLE[userPlan] || null;
  const filteredUpgradeOptions = useMemo(() => {
    if (!Array.isArray(allowedUpgradeKeys)) return upgradeOptions;
    return upgradeOptions.filter((planKey) => allowedUpgradeKeys.includes(planKey));
  }, [allowedUpgradeKeys, upgradeOptions]);

  useEffect(() => {
    let cancel = false;
    async function fetchRules() {
      if (!hasRouteAccess) return;
      const token = localStorage.getItem("__extraconomy_token");
      const username = localStorage.getItem("__extraconomy_username");
      if (!token || !username) return;

      try {
        const resRules = await getUserInfoService.getUserRules(token, username);
        if (cancel || resRules?.messageCode !== 200001) return;

        const available = (UPGRADE_OPTIONS_BY_ROLE[userPlan] || []).filter((planKey) =>
          isPlanAllowedByRules(resRules.result, planKey, userPlan),
        );
        setAllowedUpgradeKeys(available);
      } catch (e) {
        if (!cancel) setAllowedUpgradeKeys(null);
      }
    }
    fetchRules();
    return () => {
      cancel = true;
    };
  }, [getUserInfoService, hasRouteAccess, userPlan]);

  useEffect(() => {
    let cancel = false;

    async function fetchPrices() {
      if (!hasPaymentFlow) return;
      setLoadingPassword(true);
      try {
        const basePrice = await getInfoService.getPlanListByCurrency(effectiveCurrency);
        if (cancel) return;

        const currentPlanBusiness = basePrice.result.find((item) => item.id === 1);
        const currentSeller = basePrice.result.find((item) => item.id === 4);
        const currentPlanArea = basePrice.result.find((item) => item.id === 2);

        setPriceBusiness(currentPlanBusiness?.price || 0);
        setPriceSeller(userPlan === "Store" ? STORE_START_SELLER_PRICE : currentSeller?.price || 0);

        if (userPlan === "Partner")
          setPriceArea((currentPlanArea?.price || 0) - (currentPlanBusiness?.price || 0));
        else if (userPlan === "Seller")
          setPriceArea((currentPlanArea?.price || 0) - (currentSeller?.price || 0));
        else setPriceArea(currentPlanArea?.price || 0);
      } finally {
        setLoadingPassword(false);
      }
    }

    fetchPrices();
    return () => {
      cancel = true;
    };
  }, [effectiveCurrency, getInfoService, hasPaymentFlow, userPlan]);

  async function upgradePlan(planId) {
    if (planId <= 0) return;
    try {
      if (planId === 1) setLoadingBusiness(true);
      else if (planId === 2) setLoadingArea(true);
      else setLoadingSeller(true);

      let fullPricePlan;
      if (planId === 1) fullPricePlan = priceBusiness;
      else if (planId === 2) fullPricePlan = priceArea;
      else fullPricePlan = userPlan === "Store" ? STORE_START_SELLER_PRICE : priceSeller;

      if (effectiveCurrency !== process.env.REACT_APP_BASE_PAYPAL_CURRENCY) {
        const resForex = await getInfoService.getForexRate(
          effectiveCurrency,
          process.env.REACT_APP_BASE_PAYPAL_CURRENCY,
        );
        setPriceForex(parseFloat((fullPricePlan * resForex.result).toFixed(2)));
      } else {
        setPriceForex(fullPricePlan);
      }
    } catch (e) {
      console.log(e);
    } finally {
      if (planId === 1) setLoadingBusiness(false);
      else if (planId === 2) setLoadingArea(false);
      else setLoadingSeller(false);
    }
  }

  async function handleSelectPlan(planId) {
    setPlan(planId);
    setPriceForex(0);
    await upgradePlan(planId);
  }

  async function upgradeWithVoucher() {
    try {
      setLoadingArea(true);
      if (voucher.length === 0) {
        toastUpgrade.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("pleaseFillOutAll"),
          life: 6000,
        });
        setLoadingArea(false);
        return;
      }

      const resAreaManager = await areaManagerService.upgrade(
        localStorage.getItem("__extraconomy_token"),
        localStorage.getItem("__extraconomy_username"),
        voucher,
        true,
      );

      if (resAreaManager.messageCode !== 200000) {
        toastUpgrade.current.show({
          severity: "error",
          summary: localeText("error"),
          detail: localeText("data").find((msg) => msg.code === resAreaManager.messageCode).message,
          life: 6000,
        });
        setLoadingArea(false);
      } else {
        toastUpgrade.current.show({
          severity: "success",
          summary: localeText("success"),
          detail: localeText("upgradedSuccessfully"),
          life: 6000,
        });
        counterStart();
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
      }
      setLoadingArea(false);
      console.log(e);
    }
  }

  function counterStart() {
    setRedirectCounting(true);
    let timesRun = 20;
    const interval = setInterval(() => {
      setRedirectCounter(timesRun);
      timesRun -= 1;
      if (timesRun === -1) {
        clearInterval(interval);
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
      }
    }, 1000);
  }

  function handleLoginNow() {
    localStorage.removeItem("__extraconomy_token");
    localStorage.removeItem("__extraconomy_username");
    window.location.replace("/login");
  }

  function handleBackToPlans() {
    setPlan(0);
    setPriceForex(0);
    setUseVoucherCheck(false);
    setVoucher("");
  }

  const getPriceByPlan = (planKey) => {
    if (planKey === "Partner") return priceBusiness;
    if (planKey === "Seller") return priceSeller;
    if (planKey === "StartSeller") return priceSeller;
    if (planKey === "Manager") return priceArea;
    return null;
  };

  const getLoadingByPlan = (planKey) => {
    if (planKey === "Partner") return loadingBusiness;
    if (planKey === "Seller") return loadingSeller;
    if (planKey === "Manager") return loadingArea;
    return false;
  };

  if (
    !hasUpgradeAccess ||
    !hasRouteAccess ||
    (Array.isArray(allowedUpgradeKeys) && !allowedUpgradeKeys.length)
  ) {
    return (
      <DashboardLayout>
        <div className="upgrade-page-empty">
          <h2>{localeText("upgradePageNotAvailableTitle")}</h2>
          <p>{localeText("upgradePageNotAvailableDescription")}</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Toast ref={toastUpgrade} position="top-right" />
      <div className="upgrade-page-wrapper">
        <header className="upgrade-page-header">
          <h1 className="font-bold text-4xl">{localeText(`upgradePageHeadline${userPlan}`)}</h1>
          <p className="text-lg">{localeText(`upgradePageSubheadline${userPlan}`)}</p>
        </header>

        {plan > 0 && priceForex > 0 ? (
          <section className="upgrade-page-payment-wrapper">
            {!redirectCounting ? (
              <UpgradePaymentCheckoutStep
                plan={plan}
                voucher={voucher}
                useVoucherCheck={useVoucherCheck}
                loadingArea={loadingArea}
                loadingBusiness={loadingBusiness}
                loadingSeller={loadingSeller}
                loadingPassword={loadingPassword}
                userTypeInfo={userTypeInfo}
                currencyCode={effectiveCurrency}
                priceForex={priceForex}
                toastUpgrade={toastUpgrade}
                setVoucher={setVoucher}
                setUseVoucherCheck={setUseVoucherCheck}
                setLoadingPassword={setLoadingPassword}
                onUpgradeWithVoucher={upgradeWithVoucher}
                onBackToPlans={handleBackToPlans}
              />
            ) : (
              <UpgradePaymentRedirectStep
                redirectCounter={redirectCounter}
                onLoginNow={handleLoginNow}
              />
            )}
          </section>
        ) : (
          <>
            {loadingPassword && hasPaymentFlow ? (
              <div className="text-center">
                <i className="pi pi-spin pi-spinner text-8xl text-500 my-8"></i>
              </div>
            ) : (
              <section className="upgrade-page-cards-grid">
                {filteredUpgradeOptions.map((planKey) => {
                  const planConfig = PLAN_CONFIG[planKey];
                  const price = getPriceByPlan(planKey);
                  const hasPrice = hasPaymentFlow && typeof price === "number";
                  const priceLabel = hasPrice ? `${effectiveCurrency} ${formatPrice(price)}` : null;
                  const renewLabel =
                    planKey === "StartSeller" ? localeText("upgradeStartSellerRenewal") : null;

                  return (
                    <UpgradePlanCard
                      key={planKey}
                      planKey={planKey}
                      planConfig={planConfig}
                      priceLabel={priceLabel}
                      isHighlighted={highlightedPlan === planKey}
                      showMostPopular={highlightedPlan === planKey}
                      selectable={hasPaymentFlow}
                      loading={getLoadingByPlan(planKey)}
                      renewLabel={renewLabel}
                      onSelect={() => handleSelectPlan(planConfig.id)}
                    />
                  );
                })}
              </section>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default UpgradePage;
